import React from 'react';
import { cn } from '../../lib/utils';
import { formatTimePreserveTimezone, formatDateTimeWithTimezone } from '../../utils/dateTimeUtils';
import { normalizeFlightStatus, formatDelayDuration, withFlightProgress, ON_TIME_TOLERANCE_MINUTES } from '../../utils/flightDisplay';

/**
 * Flight status badge that displays the current status with appropriate colors.
 * Supports real-time updates via SSE.
 *
 * @param {string} status - Flight status (scheduled, departed, landed, arrived, etc.)
 * @param {number} delayMinutes - Delay in minutes (optional)
 * @param {string} size - Badge size: 'small' or 'default'
 * @param {string} variant - 'light', 'dark', or 'theme' (shared ui-* tokens, follows the app theme)
 * @param {string} className - Additional CSS classes
 */
// The status hue (read off its light/dark text class) as shared theme tokens.
// Class names are written out in full so Tailwind generates them.
const THEME_COLORS = {
  neutral: { bgColor: 'bg-ui-hover', textColor: 'text-ui-ink-2', borderColor: 'border-ui-control' },
  indigo: { bgColor: 'bg-ui-indigo-bg', textColor: 'text-ui-indigo-fg', borderColor: 'border-ui-indigo-line' },
  orange: { bgColor: 'bg-ui-orange-bg', textColor: 'text-ui-orange-fg', borderColor: 'border-ui-orange-line' },
  blue: { bgColor: 'bg-ui-blue-bg', textColor: 'text-ui-blue-fg', borderColor: 'border-ui-blue-line' },
  green: { bgColor: 'bg-ui-green-bg', textColor: 'text-ui-green-fg', borderColor: 'border-ui-green-line' },
  red: { bgColor: 'bg-ui-red-bg', textColor: 'text-ui-red-fg', borderColor: 'border-ui-red-line' },
  amber: { bgColor: 'bg-ui-amber-bg', textColor: 'text-ui-amber-fg', borderColor: 'border-ui-amber-line' },
};
const TOKEN_HUE = { sky: 'blue', emerald: 'green' };
const themeColors = (textColor) => {
  const palette = (textColor.match(/^text-([a-z]+)-/) || [])[1];
  return THEME_COLORS[TOKEN_HUE[palette] || palette] || THEME_COLORS.neutral;
};

const FlightStatusBadge = ({
  status,
  delayMinutes = null,
  size = 'default',
  variant = 'light',
  className = ''
}) => {
  // Normalize status to lowercase for comparison
  // Provider free text ("Arrived / Gate Arrival", "En Route / Delayed") maps to one vocabulary.
  const normalizedStatus = normalizeFlightStatus(status) || 'unknown';
  const isDark = variant === 'dark';

  // Determine display text and styling based on status
  const getStatusConfig = () => {
    switch (normalizedStatus) {
      case 'scheduled':
        return {
          label: 'Scheduled',
          bgColor: isDark ? 'bg-slate-700/80' : 'bg-slate-100',
          textColor: isDark ? 'text-slate-300' : 'text-slate-700',
          borderColor: isDark ? 'border-slate-600' : 'border-slate-200'
        };
      case 'check-in':
        return {
          label: 'Check-in',
          bgColor: isDark ? 'bg-slate-700/80' : 'bg-slate-100',
          textColor: isDark ? 'text-slate-200' : 'text-slate-700',
          borderColor: isDark ? 'border-slate-500' : 'border-slate-300'
        };
      case 'boarding':
        return {
          label: 'Boarding',
          bgColor: isDark ? 'bg-indigo-500/30' : 'bg-indigo-100',
          textColor: isDark ? 'text-indigo-300' : 'text-indigo-700',
          borderColor: isDark ? 'border-indigo-500/50' : 'border-indigo-200',
          pulse: true
        };
      case 'gate closed':
        return {
          label: 'Gate Closed',
          bgColor: isDark ? 'bg-indigo-500/30' : 'bg-indigo-100',
          textColor: isDark ? 'text-indigo-300' : 'text-indigo-700',
          borderColor: isDark ? 'border-indigo-500/50' : 'border-indigo-200'
        };
      case 'possibly cancelled':
        return {
          label: 'Possibly Cancelled',
          bgColor: isDark ? 'bg-orange-500/30' : 'bg-orange-100',
          textColor: isDark ? 'text-orange-300' : 'text-orange-700',
          borderColor: isDark ? 'border-orange-500/50' : 'border-orange-200'
        };
      case 'active':
      case 'en_route':
      case 'en route':
        return {
          label: 'In Flight',
          bgColor: isDark ? 'bg-blue-500/30' : 'bg-blue-100',
          textColor: isDark ? 'text-blue-300' : 'text-blue-700',
          borderColor: isDark ? 'border-blue-500/50' : 'border-blue-200',
          pulse: true
        };
      case 'landed':
        return {
          label: 'Landed',
          bgColor: isDark ? 'bg-emerald-500/30' : 'bg-emerald-100',
          textColor: isDark ? 'text-emerald-300' : 'text-emerald-700',
          borderColor: isDark ? 'border-emerald-500/50' : 'border-emerald-200'
        };
      case 'arrived':
        return {
          label: 'Arrived',
          bgColor: isDark ? 'bg-green-500/30' : 'bg-green-100',
          textColor: isDark ? 'text-green-300' : 'text-green-700',
          borderColor: isDark ? 'border-green-500/50' : 'border-green-200'
        };
      case 'departed':
        return {
          label: 'Departed',
          bgColor: isDark ? 'bg-sky-500/30' : 'bg-sky-100',
          textColor: isDark ? 'text-sky-300' : 'text-sky-700',
          borderColor: isDark ? 'border-sky-500/50' : 'border-sky-200'
        };
      case 'cancelled':
        return {
          label: 'Cancelled',
          bgColor: isDark ? 'bg-red-500/30' : 'bg-red-100',
          textColor: isDark ? 'text-red-300' : 'text-red-700',
          borderColor: isDark ? 'border-red-500/50' : 'border-red-200'
        };
      case 'diverted':
        return {
          label: 'Diverted',
          bgColor: isDark ? 'bg-orange-500/30' : 'bg-orange-100',
          textColor: isDark ? 'text-orange-300' : 'text-orange-700',
          borderColor: isDark ? 'border-orange-500/50' : 'border-orange-200'
        };
      case 'delayed':
        return {
          label: delayMinutes > 0 ? `Delayed ${formatDelayDuration(delayMinutes)}` : 'Delayed',
          bgColor: isDark ? 'bg-amber-500/30' : 'bg-amber-100',
          textColor: isDark ? 'text-amber-300' : 'text-amber-700',
          borderColor: isDark ? 'border-amber-500/50' : 'border-amber-200'
        };
      default:
        return {
          label: status || 'Unknown',
          bgColor: isDark ? 'bg-gray-700/80' : 'bg-gray-100',
          textColor: isDark ? 'text-gray-300' : 'text-gray-600',
          borderColor: isDark ? 'border-gray-600' : 'border-gray-200'
        };
    }
  };

  // Check if flight is delayed based on delay minutes
  const isDelayed = delayMinutes != null && delayMinutes >= ON_TIME_TOLERANCE_MINUTES;

  const config = isDelayed && ['scheduled', 'delayed', 'unknown', 'check-in', 'boarding', 'gate closed'].includes(normalizedStatus)
    ? {
        label: `Delayed ${formatDelayDuration(delayMinutes)}`,
        bgColor: delayMinutes > 60
          ? (isDark ? 'bg-red-500/30' : 'bg-red-100')
          : (isDark ? 'bg-amber-500/30' : 'bg-amber-100'),
        textColor: delayMinutes > 60
          ? (isDark ? 'text-red-300' : 'text-red-700')
          : (isDark ? 'text-amber-300' : 'text-amber-700'),
        borderColor: delayMinutes > 60
          ? (isDark ? 'border-red-500/50' : 'border-red-200')
          : (isDark ? 'border-amber-500/50' : 'border-amber-200')
      }
    : getStatusConfig();
  const colors = variant === 'theme' ? themeColors(config.textColor) : config;

  const sizeClasses = size === 'small'
    ? 'text-[10px] px-1.5 py-0.5'
    : 'text-xs px-2 py-0.5';

  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border font-medium',
        colors.bgColor,
        colors.textColor,
        colors.borderColor,
        sizeClasses,
        config.pulse && 'animate-pulse',
        className
      )}
    >
      {config.label}{isDelayed && ['landed', 'arrived', 'departed', 'en route'].includes(normalizedStatus) ? ` · ${formatDelayDuration(delayMinutes)} late` : ''}
    </span>
  );
};

/**
 * Displays gate information for a flight leg
 */
export const FlightGateInfo = ({
  departureGate,
  arrivalGate,
  departureTerminal,
  arrivalTerminal,
  showLabels = true,
  className = ''
}) => {
  if (!departureGate && !arrivalGate) {
    return null;
  }

  const formatGate = (gate, terminal) => {
    if (!gate) return null;
    if (terminal) return `T${terminal} / Gate ${gate}`;
    return `Gate ${gate}`;
  };

  return (
    <div className={cn('flex flex-col gap-1 text-sm', className)}>
      {departureGate && (
        <div className="flex items-center gap-2">
          {showLabels && <span className="text-gray-500 text-xs">Dep:</span>}
          <span className="font-medium text-gray-700">
            {formatGate(departureGate, departureTerminal)}
          </span>
        </div>
      )}
      {arrivalGate && (
        <div className="flex items-center gap-2">
          {showLabels && <span className="text-gray-500 text-xs">Arr:</span>}
          <span className="font-medium text-gray-700">
            {formatGate(arrivalGate, arrivalTerminal)}
          </span>
        </div>
      )}
    </div>
  );
};

/**
 * Displays delay information with visual indicator
 */
export const FlightDelayIndicator = ({
  delayMinutes,
  type = 'departure', // 'departure' or 'arrival'
  variant = 'light', // or 'theme': shared ui-* tokens
  className = ''
}) => {
  const themed = variant === 'theme';
  // No measured delay is not the same as on time: say nothing.
  if (delayMinutes == null || !Number.isFinite(delayMinutes)) return null;
  if (delayMinutes < ON_TIME_TOLERANCE_MINUTES) {
    return (
      <span className={cn('text-xs font-medium', themed ? 'text-ui-green-fg' : 'text-green-600', className)}>
        On Time
      </span>
    );
  }

  const delayText = `${formatDelayDuration(delayMinutes)} late`;

  const severityClass = delayMinutes > 60
    ? (themed ? 'text-ui-red-fg' : 'text-red-600')
    : delayMinutes > 30
      ? (themed ? 'text-ui-orange-fg' : 'text-orange-600')
      : (themed ? 'text-ui-amber-fg' : 'text-amber-600');

  return (
    <span className={cn('text-xs font-medium', severityClass, className)}>
      {delayText}
    </span>
  );
};

/**
 * Displays codeshare/operating carrier information
 */
export const CodeshareInfo = ({
  ticketedAirline,
  ticketedFlightNumber,
  operatingAirline,
  operatingFlightNumber,
  operatingAirlineName,
  className = ''
}) => {
  if (!operatingAirline || operatingAirline === ticketedAirline) {
    return null;
  }

  return (
    <div className={cn('text-xs text-gray-500 italic', className)}>
      Operated by {operatingAirlineName || operatingAirline}
      {operatingFlightNumber && operatingFlightNumber !== ticketedFlightNumber && (
        <span> as {operatingAirline}{operatingFlightNumber}</span>
      )}
    </div>
  );
};

/**
 * Comprehensive flight live info component showing all real-time data
 */
export const FlightLiveInfo = ({
  leg,
  compact = false,
  className = ''
}) => {
  if (!leg) return null;

  const {
    flight_status,
    delay_minutes,
    delay_departure_minutes,
    delay_arrival_minutes,
    departure_gate,
    arrival_gate,
    departure_terminal,
    arrival_terminal,
    scheduled_departure,
    estimated_departure,
    actual_departure,
    scheduled_arrival,
    estimated_arrival,
    actual_arrival,
    is_codeshare,
    operating_airline,
    operating_airline_name,
    operating_flight_number,
    airline_name,
    flight_number,
    last_fa_update
  } = withFlightProgress(leg);

  const hasLiveData = flight_status || departure_gate || arrival_gate ||
                      estimated_departure || estimated_arrival || last_fa_update;

  if (!hasLiveData && compact) {
    return null;
  }

  // Format time preserving the original timezone from the ISO string
  const formatTime = (timeStr) => {
    if (!timeStr) return null;
    return formatTimePreserveTimezone(timeStr) || null;
  };

  // Determine the best time to display
  const departureTime = actual_departure || estimated_departure || scheduled_departure;
  const arrivalTime = actual_arrival || estimated_arrival || scheduled_arrival;

  return (
    <div className={cn('space-y-2', className)}>
      {/* Status Badge */}
      {flight_status && (
        <FlightStatusBadge
          status={flight_status}
          delayMinutes={delay_minutes}
          size={compact ? 'small' : 'default'}
        />
      )}

      {/* Gate Information */}
      {(departure_gate || arrival_gate) && (
        <FlightGateInfo
          departureGate={departure_gate}
          arrivalGate={arrival_gate}
          departureTerminal={departure_terminal}
          arrivalTerminal={arrival_terminal}
          showLabels={!compact}
        />
      )}

      {/* Time Updates */}
      {!compact && (estimated_departure || estimated_arrival) && (
        <div className="text-xs space-y-1">
          {estimated_departure && scheduled_departure && (
            <div className="flex items-center gap-2">
              <span className="text-gray-500">Est. Dep:</span>
              <span className="font-medium">{formatTime(estimated_departure)}</span>
              {delay_departure_minutes > 0 && (
                <FlightDelayIndicator delayMinutes={delay_departure_minutes} type="departure" />
              )}
            </div>
          )}
          {estimated_arrival && scheduled_arrival && (
            <div className="flex items-center gap-2">
              <span className="text-gray-500">Est. Arr:</span>
              <span className="font-medium">{formatTime(estimated_arrival)}</span>
              {delay_arrival_minutes > 0 && (
                <FlightDelayIndicator delayMinutes={delay_arrival_minutes} type="arrival" />
              )}
            </div>
          )}
        </div>
      )}

      {/* Codeshare Info */}
      {is_codeshare && operating_airline && (
        <CodeshareInfo
          ticketedAirline={airline_name}
          ticketedFlightNumber={flight_number}
          operatingAirline={operating_airline}
          operatingFlightNumber={operating_flight_number}
          operatingAirlineName={operating_airline_name}
        />
      )}

      {/* Last Updated - shown in browser timezone with explicit indicator */}
      {!compact && last_fa_update && (
        <div className="text-[10px] text-gray-400">
          Updated {formatDateTimeWithTimezone(last_fa_update)}
        </div>
      )}
    </div>
  );
};

export default FlightStatusBadge;
